import type {
  AvailableMember,
  AvailableMemberSearchBy,
} from "@/hooks/useAvailbleMember";
import { AvailableMemberRow } from "./AvailableMemberRow";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { COLUMN_WIDTHS } from "@/pages/roster-management/components/rosterTable.config";
import { images } from "@/assets/img/imagesMap";
import { cn } from "@/utils/cn";
import { useRef, type UIEvent } from "react";
import LoadingDots from "@/components/LoadingDots";
import LoadingSpiner from "@/components/ui/LoadingSpiner";

interface AvailableMemberTableProps {
  members: AvailableMember[];
  highlightedNurseId?: string | null;
  selectedMembers?: Set<string>;
  onToggleSelection?: (nurseId: string) => void;
  onRangeSelection?: (nurseIds: string[]) => void;
  hasNextPage?: boolean;
  isLoading?: boolean;
  isSearching?: boolean;
  isFetchingNextPage?: boolean;
  searchTerm?: string;
  searchBy?: AvailableMemberSearchBy;
  onLoadMore?: () => void;
  className?: string;
}

/**
 * 메디통 병원 근무자 전용 Table 컴포넌트
 * Drag & Drop 제거됨 (단순 리스트 표시)
 */
export function AvailableMemberTable({
  members,
  highlightedNurseId,
  selectedMembers,
  onToggleSelection,
  onRangeSelection,
  hasNextPage = false,
  isLoading = false,
  isSearching = false,
  isFetchingNextPage = false,
  searchTerm = "",
  searchBy = "name",
  onLoadMore,
  className,
}: AvailableMemberTableProps) {
  const lastSelectedIndexRef = useRef<number | null>(null);

  const headerList = [
    { label: "선택", width: "56px" },
    { label: "No.", width: COLUMN_WIDTHS.no },
    { label: "사번", width: COLUMN_WIDTHS.empNum },
    { label: "이름", width: COLUMN_WIDTHS.name },
    { label: "소속", width: "100px" },
    { label: "직종", width: COLUMN_WIDTHS.role },
    { label: "성별", width: "60px" },
    { label: "경력", width: COLUMN_WIDTHS.experience },
    { label: "입사일", width: COLUMN_WIDTHS.joiningDate },
    { label: "생년월일", width: COLUMN_WIDTHS.joiningDate },
    { label: "연락처", width: "120px" },
  ];

  const handleRowClick = (
    nurseId: string,
    index: number,
    event: React.MouseEvent
  ) => {
    if (event.shiftKey && lastSelectedIndexRef.current !== null) {
      // Shift 클릭: 범위 선택
      const start = Math.min(lastSelectedIndexRef.current, index);
      const end = Math.max(lastSelectedIndexRef.current, index);
      const rangeIds = members.slice(start, end + 1).map((m) => m.nurse_id);
      onRangeSelection?.(rangeIds);
      // lastSelectedIndex는 유지 (연속 shift 선택 가능하도록)
    } else {
      // 일반 클릭: 단일 토글
      onToggleSelection?.(nurseId);
      lastSelectedIndexRef.current = index;
    }
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom > 80) return;
    if (!hasNextPage || isFetchingNextPage) return;
    onLoadMore?.();
  };

  return (
    <div
      className={cn(
        "border shadow-lg rounded-b-md overflow-visible ",
        className
      )}
      onScroll={handleScroll}
    >
      <Table style={{ tableLayout: "fixed" }}>
        <colgroup>
          {headerList.map((header, idx) => (
            <col key={idx} style={{ width: header.width }} />
          ))}
        </colgroup>
        <TableHeader className="sticky top-0 z-9 bg-[#F0F0F2]">
          <TableRow>
            {headerList.map((header, idx) => (
              <TableHead
                key={idx}
                className="text-center font-pretendard font-semibold text-[14px] text-text-main2"
              >
                {header.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isSearching ? (
            <TableRow>
              <TableCell
                colSpan={headerList.length}
                className="bg-white py-8 text-center font-pretendard text-sm text-muted-foreground"
              >
                <div className="flex items-center justify-center gap-3">
                  <LoadingDots
                    count={3}
                    size={5}
                    gap={5}
                    color="#C8D7F0"
                    activeColor="#0E59C6"
                    duration={900}
                    label="근무자 검색 중"
                  />
                  <span className="font-pretendard text-[13px] text-[#0E59C6]">
                    검색 중입니다...
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : isLoading ? (
            <TableRow>
              <TableCell
                colSpan={headerList.length}
                className="h-[180px] bg-white py-8 text-center font-pretendard text-sm text-muted-foreground"
              >
                <LoadingSpiner />
              </TableCell>
            </TableRow>
          ) : members.length > 0 ? (
            members.map((member, idx) => (
              <AvailableMemberRow
                key={member.nurse_id}
                member={member}
                index={idx}
                isHighlighted={member.nurse_id === highlightedNurseId}
                isSelected={selectedMembers?.has(member.nurse_id)}
                searchTerm={searchTerm}
                searchBy={searchBy}
                onRowClick={(e) => handleRowClick(member.nurse_id, idx, e)}
                onToggleSelection={onToggleSelection}
              />
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={headerList.length}
                className="text-center py-5 bg-white"
              >
                <div className="flex flex-col justify-center items-center pt-[20px] bg-white">
                  <img
                    src={images.icon_plus}
                    alt=""
                    className=""
                    style={{ height: "2em" }}
                  />
                  <div className="text-center text-[#333333] text-base! rounded-tl-[10px] rounded-tr-[10px] pt-[10px] font-pretendard">
                    등록된 인원이 없습니다.
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}
          {isFetchingNextPage ? (
            <TableRow>
              <TableCell
                colSpan={headerList.length}
                className="bg-white py-4 text-center font-pretendard text-sm text-muted-foreground"
              >
                <div className="flex items-center justify-center gap-3">
                  <LoadingDots
                    count={5}
                    size={6}
                    gap={6}
                    color="#D6D6DE"
                    activeColor="#0E59C6"
                    duration={1100}
                    label="다음 근무자 불러오는 중"
                  />
                </div>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}
